import React, { useEffect, useMemo } from 'react';
import {
    Field,
    MessageBar,
    MessageBarBody,
    Text,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
    DateRangeValidationConstraints,
    DateRangeValidationMessages,
    DateRangeValidationResult,
    getDateRangeValidationMessage,
    validateDateRange,
} from '../../../utils/dateRangeValidation';
import TeamsFilterDropdown, {
    TeamsFilterDropdownProps,
} from '../TeamsFilterDropdown/TeamsFilterDropdown';

interface DateRangeFilterProps {
    startDate: Date | null;
    endDate: Date | null;
    onStartDateChange: (date: Date | null | undefined) => void;
    onEndDateChange: (date: Date | null | undefined) => void;
    startLabel: React.ComponentProps<typeof Field>['label'];
    endLabel: React.ComponentProps<typeof Field>['label'];
    startPlaceholder?: string;
    endPlaceholder?: string;
    startMaxDate?: Date;
    endMinDate?: Date | null;
    endMaxDate?: Date;
    formatDate?: (date?: Date) => string;
    required?: boolean;
    fieldClassName?: string;
    validationConstraints?: DateRangeValidationConstraints;
    validationMessages?: DateRangeValidationMessages;
    onValidationChange?: (isValid: boolean) => void;
    teamFilterDropdownProps?: TeamsFilterDropdownProps;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    startLabel,
    endLabel,
    startPlaceholder,
    endPlaceholder,
    startMaxDate,
    endMinDate,
    endMaxDate,
    formatDate,
    required,
    fieldClassName,
    validationConstraints,
    validationMessages,
    onValidationChange,
    teamFilterDropdownProps,
}) => {
    const validation = useMemo<DateRangeValidationResult>(() => {
        return validateDateRange(startDate, endDate, validationConstraints);
    }, [endDate, startDate, validationConstraints]);

    const isValid = validation.isValid || validation.reason === 'missing';

    useEffect(() => {
        onValidationChange?.(isValid);
    }, [isValid, onValidationChange]);

    const errorMessage = useMemo(() => {
        return getDateRangeValidationMessage(
            validation,
            validationMessages ?? {}
        );
    }, [validation, validationMessages]);

    return (
        <>
            <Field label={startLabel} className={fieldClassName}>
                <DatePicker
                    placeholder={startPlaceholder}
                    value={startDate}
                    onSelectDate={onStartDateChange}
                    maxDate={startMaxDate}
                    formatDate={formatDate}
                    required={required}
                />
            </Field>

            <Field label={endLabel} className={fieldClassName}>
                <DatePicker
                    placeholder={endPlaceholder}
                    value={endDate}
                    onSelectDate={onEndDateChange}
                    minDate={endMinDate ?? undefined}
                    maxDate={endMaxDate}
                    formatDate={formatDate}
                    required={required}
                />
            </Field>

            {teamFilterDropdownProps && (
                <TeamsFilterDropdown {...teamFilterDropdownProps} />
            )}

            {errorMessage && (
                <MessageBar intent="warning">
                    <MessageBarBody>
                        <Text>{errorMessage}</Text>
                    </MessageBarBody>
                </MessageBar>
            )}
        </>
    );
};

export default DateRangeFilter;
